/* eslint-disable import/no-unresolved */
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import { Tooltip } from 'react-tooltip';
import { GET_STAKING_DETAILS } from '../../queries';

import FeatureMainBodyLayout from '../../partials/FeatureMainBodyLayout';
import { GenericLoading } from '../../../../components/Placeholders';
import { formatNumber, formatNumberWithComma } from '../../../../utilities/formatHelpers';
import { currencies, exchange } from '../../../../utilities/constants';
import { mainStakingCurrency } from '../../constants';
import { calculateRewardsHelper } from '../../helpers';
import { StakingContext } from '../../../../contexts/staking/context';

const StakingDetails = () => {
  const { t: translate } = useTranslation();

  const history = useHistory();
  const { state: locationState } = useLocation();

  const { updateShowRewardInfo } = useContext(StakingContext);

  const { data: stakingDeal, isLoading } = useQuery([GET_STAKING_DETAILS, locationState.dealId]);

  const completedDays = useMemo(() => {
    if (stakingDeal) {
      const currentDate = moment();
      const dealStartingDate = moment(stakingDeal?.created_at);
      return currentDate.diff(dealStartingDate, 'days');
    }
    return 0;
  }, [stakingDeal]);

  const isStakingDealTerminated = useMemo(() => stakingDeal?.terminated_at, [stakingDeal]);

  const durationLabel = useMemo(() => {
    if (isStakingDealTerminated) {
      return stakingDeal?.terminated_duration_in_days;
    }
    if (completedDays > stakingDeal?.duration_in_days) {
      return stakingDeal?.duration_in_days;
    }
    return completedDays;
  }, [stakingDeal, completedDays, isStakingDealTerminated]);

  const totalReward = useMemo(() => {
    if (stakingDeal) {
      const rewardsRsults = calculateRewardsHelper(
        stakingDeal?.interest_apr,
        stakingDeal?.amount * currencies[mainStakingCurrency].conversion
      );

      if (isStakingDealTerminated) {
        return rewardsRsults.daily * stakingDeal?.terminated_duration_in_days;
      }
      return rewardsRsults.daily * stakingDeal?.duration_in_days;
    }
    return 0;
  }, [stakingDeal, isStakingDealTerminated]);

  const stakingStatusTypes = {
    completed: {
      color: 'text-success',
      background: 'bg-success-light',
    },
    staking_deposit_repayment: {
      color: 'text-warning',
      background: 'bg-warning-light',
    },
    staking_reward_payment: {
      color: 'text-info',
      background: 'bg-info-light',
    },
    terminated: {
      color: 'text-danger',
      background: 'bg-danger-light',
    },
  };

  return (
    <FeatureMainBodyLayout
      withBack
      className="position-relative"
      onPrevClick={() => history.push('/dashboard/staking')}
      title={translate('staking_details_title')}>
      {isLoading && <GenericLoading isFullScreen={false} />}
      {!isLoading && stakingDeal && (
        <>
          <p className="text-shadow text-f6 mt-5">{translate('staking_amount_label')}</p>
          <p className="text-dark text-bold text-f4 mt-1">{`${formatNumberWithComma(
            formatNumber(
              stakingDeal?.amount * currencies[mainStakingCurrency].conversion,
              exchange.CRYPTO_VALUE
            )
          )} ${mainStakingCurrency.toUpperCase()}`}</p>
          <div className="bg-lighter rounded-extra p-3 mt-4 mb-1">
            <div className="d-flex flex-row justify-content-between align-items-center">
              <p className="text-dark text-f6">{translate('create_staking_apr_label')}</p>
              <p className="text-dark text-f6">{`${stakingDeal.interest_apr} %`}</p>
            </div>
            <div className="d-flex flex-row justify-content-between align-items-center mt-4 pt-2">
              <p className="text-dark text-f6">{translate('staking_details_start_date_label')}</p>
              <p className="text-dark text-f6">{`${moment(stakingDeal.created_at).format(
                'DD.MM.YYYY'
              )}`}</p>
            </div>
            <div className="d-flex flex-row justify-content-between align-items-center mt-4 pt-2">
              <p className="text-dark text-f6">{`${translate('staking_details_end_date_label')}${
                isStakingDealTerminated
                  ? ` (${translate('staking_details_terminated_status')})`
                  : ''
              }`}</p>
              <p className="text-dark text-f6">{`${moment(
                isStakingDealTerminated ? stakingDeal?.terminated_at : stakingDeal.end_at
              ).format('DD.MM.YYYY')}`}</p>
            </div>
            <div className="d-flex flex-row justify-content-between align-items-center mt-4 pt-2">
              <p className="text-dark text-f6">
                {translate('staking_details_amount_repayment_date_label')}
              </p>
              <p className="text-dark text-f6">{`${moment(
                isStakingDealTerminated ? stakingDeal?.terminated_at : stakingDeal.end_at
              )
                .add('days', isStakingDealTerminated ? 0 : 1)
                .format('DD.MM.YYYY')}`}</p>
            </div>
            <div className="d-flex flex-row justify-content-between align-items-center mt-4 pt-2 mb-1">
              <p className="text-dark text-f6">{translate('staking_details_duration_label')}</p>
              <p className="text-f6">
                <span className="text-dark">{durationLabel}</span>
                <span className="text-shadow">
                  {`/ ${translate('staking_details_days_label').replace(
                    '__DAYS_NUMBER__',
                    stakingDeal?.duration_in_days
                  )}`}
                </span>
              </p>
            </div>
            <div className="progress bg-shadow mt-2" style={{ height: '4px' }}>
              <div
                className="progress-bar bg-dark"
                style={{
                  width: `${(durationLabel * 100) / stakingDeal?.duration_in_days}%`,
                }}
              />
            </div>
            {stakingDeal?.display_state && stakingDeal?.display_state !== 'active' && (
              <div className="d-flex flex-row justify-content-between align-items-center mt-4 pt-2 mb-1">
                <p className="text-dark text-f6">{translate('staking_details_status_label')}</p>
                <div
                  className={`rounded-extra  ${
                    stakingStatusTypes[
                      isStakingDealTerminated ? 'terminated' : stakingDeal?.display_state
                    ].background
                  }`}
                  style={{
                    padding: '2px 5px',
                    borderRadius: ' 4px',
                  }}>
                  <p
                    style={{ fontFamily: 'HM Alpha Mono', fontWeight: 500, letterSpacing: '0.3px' }}
                    className={`upper-case text-f6 ${
                      stakingStatusTypes[
                        isStakingDealTerminated ? 'terminated' : stakingDeal?.display_state
                      ].color
                    }`}>
                    {translate(
                      `staking_details_${
                        isStakingDealTerminated ? 'terminated' : stakingDeal?.display_state
                      }_status`
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
          <div className="bg-lighter rounded-extra px-3 py-4 d-flex flex-row justify-content-between align-items-center mt-2">
            <p className="text-dard text-f6">{translate('staking_details_reward_label')}</p>
            <div className="d-flex flex-row justify-content-end align-items-center">
              <p className="text-success text-f5">{`${formatNumberWithComma(
                formatNumber(totalReward, exchange.CRYPTO_VALUE)
              )} ${mainStakingCurrency.toUpperCase()}`}</p>
              <div className="ms-2 cursor-pointer text-hover d-flex flex-row justify-content-center align-items-center">
                {/* mobile */}
                <i
                  aria-label="Mute volume"
                  onClick={() => updateShowRewardInfo(true)}
                  onKeyDown={() => updateShowRewardInfo(true)}
                  role="button"
                  tabIndex={-1}
                  className="d-block d-md-none icon icon-info-icon text-shadow"
                  style={{ fontSize: '1.2rem' }}
                />

                {/* browser */}
                <i
                  aria-label="Mute volume"
                  data-tooltip-id="staking-reward-info-tooltip"
                  role="button"
                  tabIndex={-1}
                  className="d-none d-md-block icon icon-info-icon text-shadow"
                  style={{ fontSize: '1.7rem' }}
                />
              </div>
            </div>
          </div>
        </>
      )}
      <Tooltip
        id="staking-reward-info-tooltip"
        place="bottom-start"
        variant="dark"
        opacity={1}
        className="d-none d-md-block p-4 rounded border-1 custom-tooltip">
        <p className="text-white text-f6">{translate('staking_reward_note')}</p>
      </Tooltip>
    </FeatureMainBodyLayout>
  );
};

export default StakingDetails;
